import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { ImageMasterService } from "src/app/Common/ImageMasterService/image-master.service";
import { NotificationMasterCreateRequestDTO } from "src/app/Common/NotificationMaster/notificationMaster.interface";
import { NotificationMasterService } from "src/app/Common/NotificationMaster/notificationMaster.service";
import {
  ERPEntityMappingDTO,
  ERPEntityMappingReadByMasterRequestDTO,
  ERPTicketActivityCreateRequestDTO,
  supportTicketCreateRequestDTO,
} from "src/app/erp/ERPEntityMapping.interface";
import { ERPEntityMappingService } from "src/app/erp/ERPEntityMapping.service";
import { HospitalMasterDTO } from "src/app/hospital/BackOffice/hospital-master/hospital-master.interface";
import { SocietyDTO } from "src/app/housing-society/Interface & Service/society/society.interface";
import {
  onboardingDocumentUploadIssue,
  onboardingSchoolDocumentUploadIssue,
} from "src/app/notificationTemplates/hospital/email";
import { onboardingSocietyDocumentUploadIssue } from "src/app/notificationTemplates/society/email";
import { SchoolMasterDTO } from "src/app/School/BackOffice/school-master/school-master.interface";
import { environment } from "src/environment/environment";

@Component({
  selector: "app-chat-bot",
  templateUrl: "./chat-bot.component.html",
  styleUrls: ["./chat-bot.component.css"],
})
export class ChatBotComponent implements OnInit {
  @Input() hospitalInfo: HospitalMasterDTO | null = null;
  @Input() schoolInfo: SchoolMasterDTO | null = null;
  @Input() societyInfo: SocietyDTO | null = null;
  @Input() window!: "document-upload" | "login-user";

  @ViewChild("chatBotContainer") chatBotContainer!: ElementRef;

  private toastr = inject(ToastrService);
  private notificationMasterServices = inject(NotificationMasterService);
  private erpEntityMappingService = inject(ERPEntityMappingService);
  private imgUploadService = inject(ImageMasterService);

  isChatWindowOpen: boolean = false;

  enteredIssue = new FormControl("");

  selectedIssue: string = "";

  erpMappingInfo: ERPEntityMappingDTO | null = null;

  suggestedIssues: string[] = [];

  attachedFile: File | null = null;

  ngOnInit(): void {
    if (this.hospitalInfo) {
      this.getCompanyInfoByMaster("Hospital", this.hospitalInfo.hospitalId);
    } else if (this.schoolInfo) {
      this.getCompanyInfoByMaster("School", this.schoolInfo.schoolId);
    } else if (this.societyInfo) {
      this.getCompanyInfoByMaster("Society", this.societyInfo.societyId);
    }

    if (this.window === "document-upload") {
      this.suggestedIssues = [
        "The upload link is not opening.",
        "The upload link has expired unexpectedly.",
        "Document upload is failing midway.",
        "PDF files or certain formats are not uploading.",
        "The wrong document was uploaded and needs replacement.",
        "Upload progress was interrupted due to internet disconnection.",
        "A “Session Not Found” error is appearing.",
      ];
    } else if (this.window === "login-user") {
      this.suggestedIssues = [];
    }
  }

  toggleChatWindow() {
    this.isChatWindowOpen = !this.isChatWindowOpen;
    if (!this.isChatWindowOpen) {
      this.enteredIssue.patchValue("");
      this.selectedIssue = "";
      this.userInfoForm.reset();
      this.attachedFile = null;
    }
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    if (!this.chatBotContainer) return;

    if (
      this.isChatWindowOpen &&
      !this.chatBotContainer.nativeElement.contains(event.target)
    ) {
      this.isChatWindowOpen = false;
      this.enteredIssue.patchValue("");
      this.selectedIssue = "";
      this.userInfoForm.reset();
      this.attachedFile = null;
    }
  }

  userInfoForm = new FormGroup({
    userName: new FormControl("", [Validators.required]),
    email: new FormControl("", [Validators.required, Validators.email]),
    contactNumber: new FormControl("", [
      Validators.required,
      Validators.pattern(/^[6-9]\d{9}$/),
    ]),
  });

  getCompanyInfoByMaster(masterType: string, masterId: number) {
    const payload: ERPEntityMappingReadByMasterRequestDTO = {
      masterType,
      masterId,
    };

    this.erpEntityMappingService
      .ERPEntityMapping_ExternalReadByMasterId(payload)
      .subscribe({
        next: (res) => {
          this.erpMappingInfo = res.items[0];
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  async raiseTheTicket(erpMappingInfo: ERPEntityMappingDTO) {
    const payload: supportTicketCreateRequestDTO = {
      projectId: 0,
      companyId: erpMappingInfo.erpId,
      branchId: erpMappingInfo.branchId,

      ticketCompanyId: erpMappingInfo.erpId,

      title: this.selectedIssue,
      tDesc: this.selectedIssue,
      tType: "Onboarding document upload issue",
      category: "",
      tagList: "",
      ticketOwner: "",
      assignedTo: "",
      ticketStatus: "Open",
      tPriority: "",
      affectsCustomer: "false",
      appVersion: "",

      dueDate: new Date(),
      estimatedDuration: "",
      actualDuration: "",
      targetDate: new Date(),
      resolutionDate: new Date(),

      department: erpMappingInfo.masterType,
      raisedBy: this.hospitalInfo
        ? this.hospitalInfo.hospitalName
        : this.schoolInfo
        ? this.schoolInfo.schoolName
        : "",

      addField3: "",
      addField4: "",
      addField5: "",

      fromCompanyId: erpMappingInfo.erpId,
      toCompanyId: environment.nishwikCompanyId,

      otherDetail1: "",
      otherDetail2: "",
      otherDetail3: "",
      otherDetail4: "",
      otherDetail5: "",
      otherDetail6: "",

      actionUser: "1",
    };

    this.erpEntityMappingService.ERPSupportTicket_Create(payload).subscribe({
      next: async (res) => {
        this.toastr.success(
          "Your ticket successfully raised to Nishwik support tream"
        );

        if (this.attachedFile) {
          const filePath = await this.uploadDocumentAndStoreURL(
            this.attachedFile
          );

          const payload: ERPTicketActivityCreateRequestDTO = {
            ticketId: res.ticketId,
            ticketComments: `<img src='${filePath}'/>`,
            actionUser: "Not Login",
          };

          this.erpEntityMappingService
            .ERPTicketActivity_Create(payload)
            .subscribe({
              next: (res) => {
                this.attachedFile = null;
              },
              error: (err) => {
                console.log(err);
              },
            });
        }

        this.selectedIssue = "";
        this.isChatWindowOpen = false;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onIssueSelect(index: number = -1) {
    if (index === -1) {
      this.selectedIssue = this.enteredIssue.value as string;
    } else {
      this.selectedIssue = this.suggestedIssues[index];
    }
  }

  sendIssueMessageToNishwikSupportForHospital(
    selectedIssue: string,
    hospitalInfo: HospitalMasterDTO,
    userInfo: any,
    uploadedImageURL: string | null = null
  ) {
    const nContent = onboardingDocumentUploadIssue(
      selectedIssue,
      hospitalInfo,
      userInfo,
      uploadedImageURL
    );
    const data: NotificationMasterCreateRequestDTO = {
      alertConfigId: environment.alertConfigId,
      // nType: "Hospital Doctor Registration Email",
      nType: "Email",
      nSubject: `Onboarding Document Upload Problem - ${hospitalInfo.hospitalName}`,
      nContent: nContent,
      nTo: "globalnishwikdev@gmail.com",
      nCc: "",
      nBcc: "",
      nStatus: "Pending",
      remarks: "",
      attachmentPath: "",
      scheduleddate: new Date(),
      retryCount: 1,
      retryDate: new Date(),
      postSendDataSourceType: "",
      postSendDataSourceDef: "",
      dbConnId: 0,
      actionUser: "Not Login",
    };

    this.notificationMasterServices
      .NotificationMaster_ExternalCreate(data)
      .subscribe({
        next: (res) => {
          this.toastr.success(
            "Your issue has been shared with our support team. We'll get back to you shortly. 🎉"
          );
          this.selectedIssue = "";
        },
        error: (err) => {
          console.log("err", err);
        },
      });
  }

  sendIssueMessageToNishwikSupportForSchool(
    selectedIssue: string,
    schoolInfo: SchoolMasterDTO,
    userInfo: any
  ) {
    const nContent = onboardingSchoolDocumentUploadIssue(
      selectedIssue,
      schoolInfo,
      userInfo
    );
    const data: NotificationMasterCreateRequestDTO = {
      alertConfigId: environment.alertConfigId,
      // nType: "Hospital Doctor Registration Email",
      nType: "Email",
      nSubject: `Onboarding Document Upload Problem - ${schoolInfo.schoolName}`,
      nContent: nContent,
      nTo: "globalnishwikdev@gmail.com",
      nCc: "",
      nBcc: "",
      nStatus: "Pending",
      remarks: "",
      attachmentPath: "",
      scheduleddate: new Date(),
      retryCount: 1,
      retryDate: new Date(),
      postSendDataSourceType: "",
      postSendDataSourceDef: "",
      dbConnId: 0,
      actionUser: "Not Login",
    };

    this.notificationMasterServices
      .NotificationMaster_ExternalCreate(data)
      .subscribe({
        next: (res) => {
          this.toastr.success(
            "Your issue has been shared with our support team. We'll get back to you shortly. 🎉"
          );
          this.selectedIssue = "";
        },
        error: (err) => {
          console.log("err", err);
        },
      });
  }

  sendIssueMessageToNishwikSupportForSociety(
    selectedIssue: string,
    societyInfo: SocietyDTO,
    userInfo: any
  ) {
    const nContent = onboardingSocietyDocumentUploadIssue(
      selectedIssue,
      societyInfo,
      userInfo
    );
    const data: NotificationMasterCreateRequestDTO = {
      alertConfigId: environment.alertConfigId,
      // nType: "Hospital Doctor Registration Email",
      nType: "Email",
      nSubject: `Onboarding Document Upload Problem - ${societyInfo.societyName}`,
      nContent: nContent,
      nTo: "globalnishwikdev@gmail.com",
      nCc: "",
      nBcc: "",
      nStatus: "Pending",
      remarks: "",
      attachmentPath: "",
      scheduleddate: new Date(),
      retryCount: 1,
      retryDate: new Date(),
      postSendDataSourceType: "",
      postSendDataSourceDef: "",
      dbConnId: 0,
      actionUser: "Not Login",
    };

    this.notificationMasterServices
      .NotificationMaster_ExternalCreate(data)
      .subscribe({
        next: (res) => {
          this.toastr.success(
            "Your issue has been shared with our support team. We'll get back to you shortly. 🎉"
          );
          this.selectedIssue = "";
        },
        error: (err) => {
          console.log("err", err);
        },
      });
  };

  uploadImage(file: File): Promise<string> {
    return new Promise((res, rej) => {
      this.imgUploadService.ImageMaster_ExternalUpload(file).subscribe({
        next: (response) => {
          res(response.fileURL); 
        },
        error: (error) => {console.error("Image upload failed:", error);
          rej(error);
        },
      });
    })
  }

  async submitForm() {
    this.userInfoForm.markAllAsTouched();

    if (this.userInfoForm.valid && this.hospitalInfo) {
      // this.raiseTheTicket(this.erpMappingInfo);

      let uploadedImageURL: string | null = null;

      if(this.attachedFile) {
        uploadedImageURL = await this.uploadImage(this.attachedFile);
      };

      await this.sendIssueMessageToNishwikSupportForHospital(
        this.selectedIssue,
        this.hospitalInfo,
        this.userInfoForm.value,
        uploadedImageURL
      );

    } else if (
      this.userInfoForm.valid &&
      this.erpMappingInfo &&
      this.schoolInfo
    ) {
      this.raiseTheTicket(this.erpMappingInfo);
      this.sendIssueMessageToNishwikSupportForSchool(
        this.selectedIssue,
        this.schoolInfo,
        this.userInfoForm.value
      );
    } else if (
      this.userInfoForm.valid &&
      this.erpMappingInfo &&
      this.societyInfo
    ) {
      this.raiseTheTicket(this.erpMappingInfo);
      this.sendIssueMessageToNishwikSupportForSociety(
        this.selectedIssue,
        this.societyInfo,
        this.userInfoForm.value
      );
    }
  }

  /* File store , generate URL and upload functions */

  onFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.attachedFile = input.files[0];
    }

    input.value = "";
  }

  uploadDocumentAndStoreURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this.imgUploadService.ImageMaster_ExternalUpload(file).subscribe({
        next: (res) => {
          const imgURL = res.fileURL;
          resolve(imgURL);
        },
        error: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  }

  /* Input validation functions */

  allowAlphabetsAndSpaceOnly(event: KeyboardEvent) {
    const char = event.key;

    const isAlphabet = /^[a-zA-Z]$/.test(char);
    const isSpace = char === " ";

    if (!isAlphabet && !isSpace) {
      event.preventDefault();
    }
  }

  onlyNumberAllowValidation(event: KeyboardEvent) {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }
}
